import { fromEvent as observableFromEvent, Observable, Subscription }               from 'rxjs';
import { debounceTime }                                                             from 'rxjs/operators';
import { Directive, ElementRef, Input, Output, EventEmitter, OnChanges, OnDestroy } from '@angular/core';
import { ComponentChanges, whenChanging }                                           from '@cs/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';


@UntilDestroy()
@Directive({selector: '[scroll-page]', standalone: false})
export class CsScrollPageDirective implements OnChanges, OnDestroy {

	private _window: Window;
	private _minY             = 100;
	private _className        = '';
	private _debounceTime     = 300;
	private _scrollHandler: Observable<Event>;
	private _scrollHandlerSubscription: Subscription;
	private _temporaryTimeout = false;
	private _observer: IntersectionObserver | null = null;
	private _observerTimeoutId: ReturnType<typeof setTimeout> | null = null;

	/**
	 * Specify the offset from the top of the page.
	 * @param  minY Set the offset in pixels
	 */
	@Input('scrollMin')
	set scrollMin(minY: number) {
		this._minY = minY || this._minY;
	}

	@Input() useElementHeight = true;

	/**
	 * CSS selector for a sentinel element inside the host. When provided the directive
	 * uses IntersectionObserver instead of scroll events — fires whenever the sentinel
	 * enters the viewport, regardless of which container (window or inner element) is
	 * scrolling. Use this for grids whose scroll container is not the window.
	 */
	@Input() scrollSelector: string | null = null;

	/**
	 * Wait time assuring the scrolling is stopped
	 * @param  debounceTime Set debounce in ms
	 */
	@Input('debounceTime')
	set debounceTime(debounceTime: number) {
		this._debounceTime = debounceTime || this._debounceTime;
	}

	/**
	 * If the default sticky class isn't sufficient, specify the override here
	 */
	@Input('scrollClass')
	set scrollClass(className: string) {
		this._className = className || this._className;
	}

	/**
	 * Flag to stop the scroll detection event firing
	 */
	@Input()
	enableScrollDetection = true;

	@Output() onScroll: EventEmitter<any> = new EventEmitter();

	constructor(private _element: ElementRef) {
		this._window        = window;
		this._scrollHandler = observableFromEvent(this._window, 'scroll');
	}

	ngOnChanges(changes: ComponentChanges<CsScrollPageDirective>): void {
		whenChanging(changes.enableScrollDetection, true).execute(value => {
			if (value.currentValue) {
				this.subscribeForScrollEvent();
			} else {
				this.cancelSubscription();
			}
		});
	}

	subscribeForScrollEvent() {
		this.cancelSubscription();

		if (this.scrollSelector) {
			// Defer so Angular finishes rendering *ngIf-controlled sentinel elements
			// before we try to query and observe them.
			this._observerTimeoutId = setTimeout(() => {
				this._observerTimeoutId = null;
				const sentinel = this._element.nativeElement.querySelector(this.scrollSelector) as HTMLElement | null;
				if (!sentinel) return;

				this._observer = new IntersectionObserver((entries) => {
					const isVisible = entries[0]?.isIntersecting ?? false;
					if (isVisible && !this._temporaryTimeout) {
						this._temporaryTimeout = true;
						this.onScroll.emit(entries[0]);
					} else if (!isVisible && this._temporaryTimeout) {
						this._temporaryTimeout = false;
					}
				}, { threshold: 0 });

				this._observer.observe(sentinel);
			}, 0);
		} else {
			const source = this.useElementHeight
				? observableFromEvent<Event>(this._element.nativeElement, 'scroll')
				: this._scrollHandler;

			this._scrollHandlerSubscription = source.pipe(
				untilDestroyed(this),
				debounceTime(this._debounceTime)
			).subscribe((e) => this.handleScrollEvent(e));
		}
	}

	handleScrollEvent(e: Event) {
		const scrollHeight = this.useElementHeight ? this._element.nativeElement.scrollHeight : document.body.scrollHeight;
		const scrollTop    = this.useElementHeight ? this._element.nativeElement.scrollTop    : this._window.pageYOffset;
		const clientHeight = this.useElementHeight ? this._element.nativeElement.clientHeight : this._window.innerHeight;

		if ((scrollTop + clientHeight >= scrollHeight - this._minY) && !this._temporaryTimeout) {
			this._temporaryTimeout = true;
			this.onScroll.emit(e);
		} else if ((scrollTop + clientHeight < scrollHeight - this._minY) && this._temporaryTimeout) {
			this._temporaryTimeout = false;
		}
	}

	ngOnDestroy(): void {
		this.cancelSubscription();
	}

	private cancelSubscription() {
		if (this._observerTimeoutId !== null) {
			clearTimeout(this._observerTimeoutId);
			this._observerTimeoutId = null;
		}
		if (this._observer) {
			this._observer.disconnect();
			this._observer = null;
		}
		this._temporaryTimeout = false;
		if (this._scrollHandlerSubscription && !this._scrollHandlerSubscription.closed)
			this._scrollHandlerSubscription.unsubscribe();
	}
}
